/**
 * 应用界面语言 (中文 / 英文).
 *
 * 只覆盖应用自身的界面文案 (导航, 我的, 弹层与轻提示), 不涉及业务内容
 * (资源标题, 词汇表标签, 后端错误消息等); 与「系统设置 → 语言设置」里的
 * 内容语言是两件事, 后者只影响跟读页面的字幕展示.
 *
 * - 首屏由 index.html 的内联脚本提前写入 html lang, 避免语言切换闪烁;
 * - 选择持久化在 localStorage, 与 store 解耦, 模板里用 `$store.i18n.t(key)`.
 */

export type Locale = 'zh' | 'en'

export const DEFAULT_LOCALE: Locale = 'zh'

const STORAGE_KEY = 'oma-gendu:locale'

const zh = {
  'common.cancel': '取消',
  'common.close': '关闭',
  'common.done': '完成',
  'common.previous': '上一页',
  'common.next': '下一页',
  'common.retry': '重试',
  'common.skipToContent': '跳到主内容',
  'common.toggleTheme': '切换深浅色',
  'common.toggleLanguage': '切换语言',

  'nav.home': '首页',
  'nav.courses': '资源',
  'nav.dictionary': '查词',
  'dictionary.search': '查词',
  'dictionary.placeholder': '输入英文单词或中文释义',
  'dictionary.clear': '清空搜索',
  'dictionary.empty': '没有找到匹配词汇，试试其他拼写或释义',
  'dictionary.results': '已显示 {shown} / {total} 个词汇',
  'dictionary.more': '加载更多',
  'dictionary.error': '查词失败，请稍后重试',
  'dictionary.recent': '最近查词',
  'dictionary.clearRecent': '清空记录',
  'dictionary.localHistory': '仅保存在当前设备，最多 8 条',
  'dictionary.guide': '两种方式，找到你想查的词',
  'dictionary.englishTitle': '输入英文',
  'dictionary.englishHint': '输入完整单词或开头几个字母，查找匹配词汇。',
  'dictionary.chineseTitle': '输入中文',
  'dictionary.chineseHint': '想不起英文怎么说？输入中文释义也能查。',
  'dictionary.detailHint': '点击搜索结果查看音标、发音和详细释义，也可以收藏留待复习。',
  'nav.favorites': '收藏',
  'nav.profile': '我的',
  'nav.bottomAria': '底部导航',
  'nav.mainAria': '主导航',

  'header.backHome': '返回首页',
  'header.tagline': '英语跟读 · 每天十分钟',
  'header.admin': '管理员',
  'header.member': '会员用户',
  'header.userMenu': '用户菜单',
  'header.activateMembership': '激活会员',
  'header.editProfile': '修改资料',
  'header.changePassword': '修改密码',
  'header.settings': '系统设置',
  'header.invite': '邀请码',
  'header.logout': '退出登录',
  'home.lastWatched': '上次跟读',
  'home.memberOnly': '会员专享',
  'home.recommended': '推荐资源',
  'home.continue': '继续跟读',
  'home.start': '开始跟读',
  'home.browse': '浏览资源',
  'home.emptyTitle': '暂无上架资源',
  'home.allResources': '全部资源',
  'home.popular': '热门资源',
  'home.moreResources': '更多资源',
  'home.courseCount': '共 {n} 个上架资源',
  'home.progress': '已跟读 {n}%',
  'home.remaining': '剩 {n} 分钟',
  'home.minutesSentences': '{minutes} 分钟 · {sentences} 句',
  'home.progressAria': '{title} 学习进度 {n}%',

  'course.minutes': '{n} 分钟',
  'course.sentences': '{n} 句',
  // 资源卡片底部信息: 英文用短标签避免折行, 中文仍展示完整文案
  'course.sentencesShort': '{n} 句',
  'course.publishedOn': '发布于 {date}',
  'course.free': '免费',

  'favorites.tabCourses': '视频资源',
  'favorites.tabSentences': '句子',
  'favorites.tabWords': '单词',
  'favorites.emptyTitle': '还没有收藏',
  'favorites.emptyCourses': '收藏过句子的资源会出现在这里。',
  'favorites.emptySentences': '在跟读页点句子卡片上的书签图标即可收藏。',
  'favorites.emptyWords': '在跟读页点单词卡片上的书签图标即可收藏。',
  'favorites.browse': '去逛资源',
  'favorites.wordComingSoon': '单词功能开发中',
  'favorites.removeWordAria': '取消收藏单词',
  'favorites.removeSentenceAria': '取消收藏句子',

  'courses.all': '全部',
  'courses.loadingMore': '加载中…',
  'courses.empty': '还没有已上架的资源',
  'courses.search': '搜索资源',
  'courses.searchPlaceholder': '搜索中英文标题或描述',
  'courses.clearSearch': '清空搜索',
  'courses.searchResults': '“{keyword}” · {n} 个资源',
  'courses.noSearchResults': '没有匹配的资源, 试试其他关键词或标签',


  'profile.memberStatus': '会员状态',
  'profile.memberActive': '生效中',
  'profile.memberExpired': '已失效',
  'profile.memberUntil': '有效期至',
  'profile.activateTitle': '激活会员',
  'profile.activateDesc': '输入卡券号开通或续期会员',
  'profile.inviteTitle': '邀请码',
  'profile.inviteDesc': '点击邀请码复制邀请信息',
  'profile.settingsTitle': '系统设置',
  'profile.settingsDesc': '配置单词高亮的等级与词表',
  'profile.securityTitle': '账号安全',
  'profile.securityDesc': '修改密码需要验证当前密码',
  'profile.logout': '退出登录',
  'profile.editAria': '编辑资料',
  'profile.changePasswordAria': '修改密码',
  'profile.copyInviteAria': '复制邀请信息',
  'profile.viewInviteRecordsAria': '查看邀请记录',
  'profile.avatarAria': '头像 {name}',

  'profileEdit.title': '编辑资料',
  'profileEdit.nickname': '昵称',
  'profileEdit.nicknamePlaceholder': '给自己起个名字',
  'profileEdit.save': '保存昵称',

  'password.title': '修改密码',
  'password.current': '当前密码',
  'password.new': '新密码 (至少 8 位)',
  'password.confirm': '确认新密码',
  'password.hint': '两次输入需一致, 至少 8 位',
  'password.submit': '确认修改',

  'logout.title': '退出登录',
  'logout.message': '确认退出当前账号吗?',
  'logout.confirm': '退出登录',

  'settings.title': '系统设置',
  'settings.highlightSection': '高亮配置',
  'settings.highlightDesc': '选择目标词表，自动排除先修词汇。例如 TOEFL 排除四级、六级词汇；多选合并各目标结果，命中数量不限。',
  'settings.levelsAria': '难度等级',
  'settings.loadingAria': '加载词表设置',
  'settings.emptyTags': '这一等级暂无可选词表',
  'settings.languageSection': '语言设置',
  'settings.languageDesc': '选择内容语言, 双语模式下仅跟读页同时显示中英, 其他页面仍显示英文',
  'settings.level.beginner': '初阶',
  'settings.level.intermediate': '中阶',
  'settings.level.advanced': '高阶',

  'language.english': '英文',
  'language.chinese': '中文',
  'language.bilingual': '双语',

  'invite.title': '邀请码',
  'invite.description': '好友首次注册时填写你的邀请码，你可获赠 1 周会员，好友获赠 1 天',
  'invite.copy': '复制邀请信息',
  'invite.records': '邀请记录',
  'invite.loadingAria': '加载邀请记录',
  'invite.empty': '暂无邀请记录',
  'invite.colIndex': '序号',
  'invite.colEmail': '邮箱',
  'invite.colDate': '注册日期',

  'redeem.title': '激活会员',
  'redeem.tabsAria': '卡券激活',
  'redeem.tabRedeem': '激活',
  'redeem.tabHistory': '核销记录',
  'redeem.description': '输入卡券号激活或续期会员，时长可叠加；核销次数以卡券类型为准。',
  'redeem.codeLabel': '卡券号',
  'redeem.memberActive': '当前会员有效中, 继续激活可叠加时长',
  'redeem.submit': '立即激活',
  'redeem.historyDescription': '卡券核销及自动赠送的会员时长均记录在这里。',
  'redeem.emptyHistory': '暂无核销记录',
  'redeem.colDate': '日期',
  'redeem.colDuration': '额度',
  'redeem.colStatus': '状态',
  'redeem.colNote': '备注',
  'redeem.statusRedeemed': '已核销',

  'duration.day': '1 天',
  'duration.week': '1 周',
  'duration.month': '1 月',
  'duration.year': '1 年',
  'duration.days': '{n} 天',

  'toast.languageUpdated': '语言设置已更新',
  'toast.languageSaveFailed': '语言设置保存失败, 请重试',
  'toast.saveFailed': '保存失败, 请重试',
  'toast.nicknameUpdated': '昵称已更新',
  'toast.passwordUpdated': '密码已修改',
  'toast.inviteNotReady': '邀请码还没准备好, 请刷新后重试',
  'toast.inviteCopied': '邀请信息已复制',
  'toast.copyFailed': '复制失败, 请长按选择文本',
  'toast.redeemSuccess': '核销成功，会员时长已更新',
  'toast.highlightLoadFailed': '高亮配置加载失败',
  'toast.inviteLoadFailed': '邀请记录加载失败',
  'toast.historyLoadFailed': '核销记录加载失败，请重试',
} as const

export type MessageKey = keyof typeof zh

const en: Record<MessageKey, string> = {
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.done': 'Done',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.retry': 'Retry',
  'common.skipToContent': 'Skip to main content',
  'common.toggleTheme': 'Toggle theme',
  'common.toggleLanguage': 'Switch language',

  'nav.home': 'Home',
  'nav.courses': 'Resources',
  'nav.dictionary': 'Dictionary',
  'dictionary.search': 'Look up a word',
  'dictionary.placeholder': 'English word or Chinese meaning',
  'dictionary.clear': 'Clear search',
  'dictionary.empty': 'No matches. Try another spelling or meaning.',
  'dictionary.results': 'Showing {shown} of {total} words',
  'dictionary.more': 'Load more',
  'dictionary.error': 'Search failed. Please try again.',
  'dictionary.recent': 'Recent searches',
  'dictionary.clearRecent': 'Clear history',
  'dictionary.localHistory': 'Saved on this device only, up to 8 searches',
  'dictionary.guide': 'Two ways to find a word',
  'dictionary.englishTitle': 'Search in English',
  'dictionary.englishHint': 'Enter a word or its first few letters to find matches.',
  'dictionary.chineseTitle': 'Search in Chinese',
  'dictionary.chineseHint': 'Know the meaning but not the word? Try Chinese.',
  'dictionary.detailHint': 'Open a result for phonetics, audio and full definitions. Save words to review later.',
  'nav.favorites': 'Saved',
  'nav.profile': 'Me',
  'nav.bottomAria': 'Bottom navigation',
  'nav.mainAria': 'Main navigation',

  'header.backHome': 'Back to home',
  'header.tagline': 'Shadowing practice · 10 min a day',
  'header.admin': 'Admin',
  'header.member': 'Member',
  'header.userMenu': 'User menu',
  'header.activateMembership': 'Activate membership',
  'header.editProfile': 'Edit profile',
  'header.changePassword': 'Change password',
  'header.settings': 'Settings',
  'header.invite': 'Invite code',
  'header.logout': 'Sign out',
  'home.lastWatched': 'Last watched',
  'home.memberOnly': 'Members only',
  'home.recommended': 'Recommended',
  'home.continue': 'Continue',
  'home.start': 'Start',
  'home.browse': 'Browse resources',
  'home.emptyTitle': 'No resources yet',
  'home.allResources': 'All resources',
  'home.popular': 'Popular now',
  'home.moreResources': 'More resources',
  'home.courseCount': '{n} resources',
  'home.progress': '{n}% watched',
  'home.remaining': '{n} min left',
  'home.minutesSentences': '{minutes} min · {sentences} sentences',
  'home.progressAria': '{title} progress {n}%',

  'course.minutes': '{n}min',
  'course.sentences': '{n} sentences',
  'course.sentencesShort': '{n}s',
  'course.publishedOn': '{date}',
  'course.free': 'Free',

  'favorites.tabCourses': 'Videos',
  'favorites.tabSentences': 'Sentences',
  'favorites.tabWords': 'Words',
  'favorites.emptyTitle': 'Nothing saved yet',
  'favorites.emptyCourses': 'Resources with saved sentences appear here.',
  'favorites.emptySentences': 'Tap the bookmark on a sentence card to save it.',
  'favorites.emptyWords': 'Tap the bookmark in a word card to save it.',
  'favorites.browse': 'Browse resources',
  'favorites.wordComingSoon': 'Word saving is coming soon',
  'favorites.removeWordAria': 'Remove saved word',
  'favorites.removeSentenceAria': 'Remove saved sentence',

  'courses.all': 'All',
  'courses.loadingMore': 'Loading…',
  'courses.empty': 'No resources published yet',
  'courses.search': 'Search resources',
  'courses.searchPlaceholder': 'Search titles or descriptions in English or Chinese',
  'courses.clearSearch': 'Clear search',
  'courses.searchResults': 'Results for “{keyword}”: {n}',
  'courses.noSearchResults': 'No matching resources. Try another keyword or tag.',


  'profile.memberStatus': 'Membership',
  'profile.memberActive': 'Active',
  'profile.memberExpired': 'Inactive',
  'profile.memberUntil': 'Valid until',
  'profile.activateTitle': 'Activate membership',
  'profile.activateDesc': 'Redeem a code to start or renew your membership',
  'profile.inviteTitle': 'Invite code',
  'profile.inviteDesc': 'Tap the code to copy your invite',
  'profile.settingsTitle': 'Settings',
  'profile.settingsDesc': 'Configure word highlight levels and lists',
  'profile.securityTitle': 'Account security',
  'profile.securityDesc': 'Changing your password requires the current one',
  'profile.logout': 'Sign out',
  'profile.editAria': 'Edit profile',
  'profile.changePasswordAria': 'Change password',
  'profile.copyInviteAria': 'Copy invite',
  'profile.viewInviteRecordsAria': 'View invite records',
  'profile.avatarAria': 'Avatar {name}',

  'profileEdit.title': 'Edit profile',
  'profileEdit.nickname': 'Nickname',
  'profileEdit.nicknamePlaceholder': 'Add a nickname',
  'profileEdit.save': 'Save nickname',

  'password.title': 'Change password',
  'password.current': 'Current password',
  'password.new': 'New password (min. 8 characters)',
  'password.confirm': 'Confirm new password',
  'password.hint': 'Both entries must match and be at least 8 characters',
  'password.submit': 'Update password',

  'logout.title': 'Sign out',
  'logout.message': 'Sign out of this account?',
  'logout.confirm': 'Sign out',

  'settings.title': 'Settings',
  'settings.highlightSection': 'Word highlights',
  'settings.highlightDesc': 'Choose target lists. Prerequisite words are excluded (e.g. CET-4 and CET-6 for TOEFL). Multiple selections combine their results, with no highlight limit.',
  'settings.levelsAria': 'Difficulty levels',
  'settings.loadingAria': 'Loading word lists',
  'settings.emptyTags': 'No word lists for this level yet',
  'settings.languageSection': 'Content language',
  'settings.languageDesc':
    'Choose the content language. Bilingual only affects the shadowing page; other pages stay in English.',
  'settings.level.beginner': 'Beginner',
  'settings.level.intermediate': 'Intermediate',
  'settings.level.advanced': 'Advanced',

  'language.english': 'English',
  'language.chinese': 'Chinese',
  'language.bilingual': 'Bilingual',

  'invite.title': 'Invite code',
  'invite.description':
    'When a friend signs up with your code, you get 1 week of membership and they get 1 day.',
  'invite.copy': 'Copy invite',
  'invite.records': 'Invite records',
  'invite.loadingAria': 'Loading invite records',
  'invite.empty': 'No invite records yet',
  'invite.colIndex': '#',
  'invite.colEmail': 'Email',
  'invite.colDate': 'Registered',

  'redeem.title': 'Activate membership',
  'redeem.tabsAria': 'Coupon redemption',
  'redeem.tabRedeem': 'Redeem',
  'redeem.tabHistory': 'History',
  'redeem.description':
    'Enter a coupon code to activate or renew your membership. Durations stack; the number of uses depends on the coupon type.',
  'redeem.codeLabel': 'Coupon code',
  'redeem.memberActive': 'Your membership is active; redeeming again extends it',
  'redeem.submit': 'Redeem now',
  'redeem.historyDescription': 'Coupon redemptions and gifted membership time are recorded here.',
  'redeem.emptyHistory': 'No redemption records yet',
  'redeem.colDate': 'Date',
  'redeem.colDuration': 'Duration',
  'redeem.colStatus': 'Status',
  'redeem.colNote': 'Note',
  'redeem.statusRedeemed': 'Redeemed',

  'duration.day': '1 day',
  'duration.week': '1 week',
  'duration.month': '1 month',
  'duration.year': '1 year',
  'duration.days': '{n} days',

  'toast.languageUpdated': 'Language setting updated',
  'toast.languageSaveFailed': 'Failed to save language setting, please retry',
  'toast.saveFailed': 'Failed to save, please retry',
  'toast.nicknameUpdated': 'Nickname updated',
  'toast.passwordUpdated': 'Password updated',
  'toast.inviteNotReady': 'Invite code is not ready yet, please refresh and retry',
  'toast.inviteCopied': 'Invite copied',
  'toast.copyFailed': 'Copy failed, please select and copy the text manually',
  'toast.redeemSuccess': 'Redeemed successfully, membership updated',
  'toast.highlightLoadFailed': 'Failed to load highlight settings',
  'toast.inviteLoadFailed': 'Failed to load invite records',
  'toast.historyLoadFailed': 'Failed to load redemption records, please retry',
}

const MESSAGES: Record<Locale, Record<MessageKey, string>> = { zh, en }

/** 难度等级的后端值 → 文案 key (后端值是业务契约, 不随界面语言变化) */
const LEVEL_KEYS: Record<string, MessageKey> = {
  beginner: 'settings.level.beginner',
  intermediate: 'settings.level.intermediate',
  advanced: 'settings.level.advanced',
}

/**
 * 资源标签的中英对照.
 *
 * 后端只存中文主标签(见 app/models/course.py:COURSE_MAIN_TAGS), 展示时按界面语言翻译;
 * AI / VLOG 本身就是英文且更易识别, 保持原样。未收录的标签直接显示原文(后端新增标签也不会变成空白)。
 */
const TAG_LABELS_EN: Record<string, string> = {
  历史: 'History',
  政治: 'Politics',
  文化: 'Culture',
  心理: 'Psychology',
  励志: 'Motivation',
  媒体: 'Media',
  访谈: 'Interviews',
  教育: 'Education',
  科技: 'Tech',
  健康: 'Health',
  艺术: 'Art',
  生活: 'Lifestyle',
  运动: 'Sports',
  卡通: 'Cartoon',
  小猪佩奇: 'Peppa Pig',
  老友记: 'Friends',
}

/** 按语言翻译资源标签; 未收录的标签(含 AI / VLOG)原样返回 */
export function tagLabel(tag: string, locale: Locale): string {
  return locale === 'en' ? (TAG_LABELS_EN[tag] ?? tag) : tag
}

const TITLES: Record<Locale, string> = {
  zh: '汇智英语 · 英语跟读',
  en: 'SageHub · Shadowing Practice',
}

/** 对外品牌名: 中文保留汇智英语, 英文使用 SageHub */
const BRAND_NAMES: Record<Locale, string> = {
  zh: '汇智英语',
  en: 'SageHub',
}

const HTML_LANGS: Record<Locale, string> = {
  zh: 'zh-CN',
  en: 'en',
}

export function isLocale(value: string | null | undefined): value is Locale {
  return value === 'zh' || value === 'en'
}

export function resolveInitialLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY)
  return isLocale(stored) ? stored : DEFAULT_LOCALE
}

/** 落地语言: html lang, 标题与持久化; 首屏内联脚本读同一个 storage key */
export function applyLocale(locale: Locale): void {
  document.documentElement.lang = HTML_LANGS[locale]
  document.title = TITLES[locale]
  localStorage.setItem(STORAGE_KEY, locale)
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  )
}

export function translate(
  locale: Locale,
  key: MessageKey,
  params?: Record<string, string | number>,
): string {
  return interpolate(MESSAGES[locale][key] ?? zh[key] ?? key, params)
}

export interface I18nStore {
  locale: Locale
  /** 当前语言下的品牌名 (SageHub / 汇智英语) */
  readonly brand: string
  t(key: MessageKey, params?: Record<string, string | number>): string
  toggle(): void
  setLocale(locale: Locale): void
  /** 难度等级: 未识别的后端值退回后端自带文案 */
  levelLabel(value: string, fallback: string): string
  /** 资源标签: 英文界面显示译名, 其余保持后端原文 */
  tagLabel(tag: string): string
}

export function i18nStore(): I18nStore {
  return {
    locale: resolveInitialLocale(),

    get brand() {
      return BRAND_NAMES[this.locale]
    },

    t(key, params) {
      return translate(this.locale, key, params)
    },

    toggle() {
      this.setLocale(this.locale === 'zh' ? 'en' : 'zh')
    },

    setLocale(locale) {
      this.locale = locale
      applyLocale(locale)
    },

    levelLabel(value, fallback) {
      const key = LEVEL_KEYS[value]
      return key ? this.t(key) : fallback
    },

    tagLabel(tag) {
      return tagLabel(tag, this.locale)
    },
  }
}

/**
 * 非模板代码 (页面组件 / 轻提示) 取文案.
 *
 * 每次调用都从 Alpine store 读当前语言, 保证切换后立即生效;
 * store 尚未注册时用持久化的选择兜底.
 */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  const store = window.Alpine?.store('i18n') as I18nStore | undefined
  return translate(store?.locale ?? resolveInitialLocale(), key, params)
}

/** 非模板代码取资源标签文案 (标签筛选条等) */
export function tTag(tag: string): string {
  const store = window.Alpine?.store('i18n') as I18nStore | undefined
  return tagLabel(tag, store?.locale ?? resolveInitialLocale())
}

/** 非模板代码取品牌名 (邀请文案等) */
export function brandName(): string {
  const store = window.Alpine?.store('i18n') as I18nStore | undefined
  return BRAND_NAMES[store?.locale ?? resolveInitialLocale()]
}
